import React, { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, PromptCapsule } from "./fx";

// ShelveBooks — colour-matching: drag each storybook onto the shelf slot whose
// colour matches its cover. Faithful port of iOS CityHelperShelveView.

const SLOT_UNITS = [
  { x: 0.34, y: 0.4 },
  { x: 0.63, y: 0.4 },
  { x: 0.34, y: 0.66 },
  { x: 0.63, y: 0.66 },
];

const ShelveBooks = ({ payload, sounds, onDone }) => {
  const books = payload.books;
  const doneRef = useRef(false);
  const stageRef = useRef(null);
  const slotRefs = useRef({});

  // Assign each slot position a (shuffled) book colour to match.
  const slots = useMemo(() => {
    const shuffled = [...books];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return SLOT_UNITS.slice(0, shuffled.length).map((unit, i) => ({
      id: shuffled[i].id,
      asset: shuffled[i].asset,
      unit,
      tint: shuffled[i].tint,
    }));
  }, [books]);

  const [shelved, setShelved] = useState(() => new Set());
  const [dragging, setDragging] = useState(null); // {id, asset, x, y}
  const [highlight, setHighlight] = useState(null);
  const [wiggle, setWiggle] = useState(null);
  const [burst, setBurst] = useState({ key: 0, x: 0, y: 0 });

  const nearestSlot = useCallback((clientX, clientY) => {
    let best = null;
    let bestD = Infinity;
    Object.entries(slotRefs.current).forEach(([id, el]) => {
      if (!el || shelved.has(id)) return;
      const r = el.getBoundingClientRect();
      const inside =
        clientX >= r.left - 16 && clientX <= r.right + 16 && clientY >= r.top - 16 && clientY <= r.bottom + 16;
      const d = Math.hypot(clientX - (r.left + r.width / 2), clientY - (r.top + r.height / 2));
      if (inside && d < bestD) {
        bestD = d;
        best = id;
      } else if (!best && d < bestD && d < r.width * 1.3) {
        bestD = d;
        best = id;
      }
    });
    return best;
  }, [shelved]);

  const place = useCallback(
    (book, slotId) => {
      const next = new Set(shelved).add(book.id);
      setShelved(next);
      sounds.playPop(next.size / Math.max(books.length, 1));
      sounds.playSparkle();
      const stage = stageRef.current?.getBoundingClientRect();
      const el = slotRefs.current[slotId];
      if (stage && el) {
        const r = el.getBoundingClientRect();
        setBurst((b) => ({ key: b.key + 1, x: r.left + r.width / 2 - stage.left, y: r.top + r.height / 2 - stage.top }));
      }
      if (next.size >= books.length && !doneRef.current) {
        doneRef.current = true;
        window.setTimeout(() => onDone(), 650);
      }
    },
    [books.length, onDone, shelved, sounds]
  );

  const handleDown = useCallback((e, book) => {
    if (shelved.has(book.id)) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const stage = stageRef.current?.getBoundingClientRect();
    setDragging({ id: book.id, asset: book.asset, x: e.clientX - (stage?.left || 0), y: e.clientY - (stage?.top || 0) });
  }, [shelved]);

  const handleMove = useCallback(
    (e, book) => {
      setDragging((d) => {
        if (!d || d.id !== book.id) return d;
        const stage = stageRef.current?.getBoundingClientRect();
        return { ...d, x: e.clientX - (stage?.left || 0), y: e.clientY - (stage?.top || 0) };
      });
      setHighlight(nearestSlot(e.clientX, e.clientY));
    },
    [nearestSlot]
  );

  const handleUp = useCallback(
    (e, book) => {
      const slotId = nearestSlot(e.clientX, e.clientY);
      setDragging(null);
      setHighlight(null);
      if (!slotId) return;
      if (slotId === book.id) {
        place(book, slotId);
      } else {
        sounds.playThud();
        setWiggle(`${slotId}-${Date.now()}`);
      }
    },
    [nearestSlot, place, sounds]
  );

  return (
    <div className="ch-activity-body ch-shelve">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div className="ch-stage" ref={stageRef}>
        <div className="ch-shelf">
          <Sprite name={payload.shelfAsset || "ch_bookshelf"} className="ch-shelf-img" />
          {slots.map((slot) => {
            const filled = shelved.has(slot.id);
            const hot = highlight === slot.id;
            return (
              <motion.div
                key={slot.id}
                className={`ch-shelf-slot ${hot ? "is-hot" : ""} ${filled ? "is-filled" : ""}`}
                style={{ left: `${slot.unit.x * 100}%`, top: `${slot.unit.y * 100}%`, "--ch-slot-tint": slot.tint }}
                ref={(el) => (slotRefs.current[slot.id] = el)}
                animate={wiggle && wiggle.startsWith(slot.id) ? { x: [0, -7, 7, -5, 5, 0] } : {}}
                transition={{ duration: 0.4 }}
              >
                {filled ? <Sprite name={slot.asset} className="ch-shelf-book" /> : <span className="ch-slot-hint" />}
              </motion.div>
            );
          })}
        </div>

        {dragging && (
          <img
            src={`/assets/games/city-helpers/${dragging.asset}.png`}
            alt=""
            aria-hidden="true"
            draggable="false"
            className="ch-shelf-drag"
            style={{ left: dragging.x, top: dragging.y }}
          />
        )}

        <SparkleBurst trigger={burst.key} reach={70} style={{ left: burst.x, top: burst.y }} />
      </div>

      <div className="ch-book-tray">
        {books.map((book, idx) =>
          shelved.has(book.id) ? null : (
            <div
              key={book.id}
              className="ch-tray-book"
              role="button"
              tabIndex={-1}
              aria-label={`${book.id} book`}
              style={{ opacity: dragging?.id === book.id ? 0.25 : 1 }}
              onPointerDown={(e) => handleDown(e, book)}
              onPointerMove={(e) => handleMove(e, book)}
              onPointerUp={(e) => handleUp(e, book)}
            >
              <motion.span
                style={{ display: "block", width: "100%", height: "100%" }}
                initial={{ scale: 0, y: 16, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 250, damping: 15, delay: 0.15 + idx * 0.1 }}
              >
                <Sprite name={book.asset} />
              </motion.span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ShelveBooks;
